import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';

// Hook to detect theme
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return !document.documentElement.classList.contains('light');
    }
    return true;
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(!document.documentElement.classList.contains('light'));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isDark = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className="fixed top-0 w-full z-50 transition-all dark-transition backdrop-blur-md"
      style={{ 
        borderBottom: '1px solid var(--border-subtle)',
        background: isScrolled 
          ? (isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)')
          : (isDark ? 'transparent' : 'rgba(255, 255, 255, 0.8)')
      }}
    >
      <div className="mx-auto px-[7.6923%] py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 dark-transition hover:opacity-80">
          <div className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>
            American Prime
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-text dark-transition hover:opacity-100 ${
                location.pathname === link.path
                  ? 'opacity-100'
                  : 'opacity-60'
              }`}
              style={{
                color:
                  location.pathname === link.path
                    ? 'var(--brand-primary)'
                    : 'var(--text-primary)'
              }}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center space-x-4">
          <ThemeToggle />
          <a
            href="tel:+12035899774"
            className="body-medium dark-transition hover:opacity-80"
            style={{ color: 'var(--text-secondary)' }}
          >
            +1 (203) 589-9774
          </a>
          <Link
            to="/contact"
            className="button-text dark-button-animate"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              padding: '12px 24px',
              borderRadius: '0px',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{ color: 'var(--text-primary)' }}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden"
          style={{
            background: 'var(--bg-primary)',
            borderTop: '1px solid var(--border-subtle)',
            padding: '20px 7.6923%'
          }}
        >
          <nav className="flex flex-col space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="nav-text dark-transition"
                style={{
                  color:
                    location.pathname === link.path
                      ? 'var(--brand-primary)'
                      : 'var(--text-primary)'
                }}
              >
                {link.name}
              </Link>
            ))}
            <a
              href="tel:+12035899774"
              className="body-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              +1 (203) 589-9774
            </a>
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="button-text dark-button-animate text-center"
              style={{
                background: 'var(--brand-primary)',
                color: '#000000',
                padding: '12px 24px',
                borderRadius: '0px',
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Get Started
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
